import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import Seo from '../components/Seo';
import { IMAGES, APPROACHES, INSURERS, CLINIC } from '../mock';

const Home = () => {
  return (
    <div>
      <Seo
        title="Virtual Therapy for Insomnia, Anxiety & Depression"
        description="The Mood & Sleep Clinic offers specialized virtual therapy for insomnia, anxiety, depression, PTSD and more. Led by Dr. Bill Hoekstra, Psy.D. — PSYPACT authorized across 44 states. Recovery is here."
        path="/"
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center">
          <h1 className="font-heading font-light text-navy text-4xl sm:text-5xl md:text-6xl tracking-wide fade-up">
            Build Your Resilience
          </h1>
          <p className="mt-4 font-heading text-[#3d6a9c] text-lg sm:text-xl tracking-[0.15em] fade-up">
            anxiety &nbsp;·&nbsp; trauma &nbsp;·&nbsp; depression &nbsp;·&nbsp; sleep troubles
          </p>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="relative order-2 md:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img src={IMAGES.groupOutdoors} alt="A diverse, happy group of adults sitting together outdoors, representing renewed mental wellbeing" className="w-full h-[360px] sm:h-[420px] object-cover" width="900" height="600" fetchpriority="high" />
              </div>
            </div>
            <div className="order-1 md:order-2">
              <h2 className="font-heading font-light text-navy text-2xl sm:text-3xl leading-snug">
                Exceptional care, delivered with compassion.
              </h2>
              <p className="mt-5 text-muted-foreground leading-relaxed">
                The Mood &amp; Sleep Clinic provides virtual mental health services with exceptional care and compassion. My commitment to quality care and effective treatment makes me ideal for therapy addressing insomnia, anxiety, PTSD, depression, compulsive behaviors, and more. Explore my services to see how I can help you achieve optimal mental health.
              </p>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 mt-7 rounded-full bg-navy text-white px-7 py-3 font-heading font-medium tracking-wide hover:bg-[#2c4d76] transition-all hover:gap-3"
              >
                Explore Services <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Evidence-based approaches */}
      <section className="bg-[#f4f8fc] py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-heading font-light text-navy text-3xl sm:text-4xl">Evidence-Based Therapy Approaches</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Every person deserves strong emotional health and good sleep. For more than 24 years, I have delivered effective care for anxiety, depression, PTSD, panic attacks, compulsive behavior, substance use urges, insomnia, and many other challenges. Contact me today to learn how I can help improve you, your mood, and your sleep. I can&rsquo;t wait to meet you!
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {APPROACHES.map((a) => (
              <div key={a.abbr} className="bg-white rounded-2xl p-7 shadow-sm border border-border hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="font-heading text-2xl font-semibold text-navy">{a.abbr}</div>
                <div className="text-sm font-medium text-[#3d6a9c] mt-1">{a.name}</div>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance */}
      <section className="py-20 bg-[#f4f8fc]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-light text-navy text-3xl sm:text-4xl">In-Network Insurance</h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            The Mood &amp; Sleep Clinic is a private practice that is an in-network provider with the following plans. Many clients also pay through an HSA.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 mt-12">
            {INSURERS.map((name) => (
              <div
                key={name}
                className="bg-white rounded-xl border border-border shadow-sm py-7 px-4 flex flex-col items-center justify-center gap-2 hover:shadow-md transition-shadow"
              >
                <ShieldCheck size={22} className="text-[#3d6a9c]" />
                <span className="font-heading font-medium text-navy text-sm sm:text-base">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-navy">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="font-heading font-light text-white text-3xl sm:text-4xl">Begin Your Recovery Today</h2>
          <p className="mt-4 text-white/75 max-w-2xl mx-auto">
            Take the first step toward better mood and better sleep. Reach out for a free initial consultation — I&rsquo;ll help you find the treatment that fits your needs best.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/getting-started" className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-navy px-7 py-3 font-heading font-medium hover:bg-[#eaf3fb] transition-colors">
              Get Started
            </Link>
            <a href={CLINIC.phoneHref} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/50 text-white px-7 py-3 font-heading font-medium hover:bg-white/10 transition-colors">
              Call {CLINIC.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
